import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isEmbedMode } from '../utils/embedAuth';

const UnauthorizedPage = () => {
  const { login, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [error, setError] = useState(null);
  const loginInitiatedRef = useRef(false);

  // Authenticated users only belong here when RequireAuth sent them with
  // state.denied (signed in but lacking permission) — they must SEE this
  // page, not get silently bounced home. Anyone else who is signed in
  // (post-login callback landing, stale bookmark, back button) goes back to
  // where they were headed, or home.
  useEffect(() => {
    if (!loading && user && !location.state?.denied) {
      navigate(location.state?.from?.pathname || '/', { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  const handleLogin = async () => {
    if (loginInitiatedRef.current) {
      console.log('[UnauthorizedPage] Login already in progress, skipping...');
      return;
    }

    try {
      loginInitiatedRef.current = true;
      setLoginAttempted(true);
      setError(null);
      console.log('[UnauthorizedPage] Initiating login...');
      const from = location.state?.from;
      await login(from ? `${from.pathname || '/'}${from.search || ''}` : undefined);
      // Don't navigate here - wait for user state to update via useEffect above
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message || 'Login failed. Please try again.');
      setLoginAttempted(false);
      loginInitiatedRef.current = false;
    }
  };

  // Auto-trigger login once when no user is signed in (standalone only).
  // login() picks the method itself: MSAL when configured, else the Easy Auth
  // /.auth/login/aad redirect. Signed-out visitors therefore bounce straight
  // to AAD — same UX the "Require authentication" front door used to provide —
  // and only see the loading state below, never the Access Denied copy.
  useEffect(() => {
    // Only run once when component mounts and conditions are met
    // Use a check to ensure we don't run multiple times
    if (loginInitiatedRef.current) {
      return; // Already attempted, don't retry
    }

    if (!loading && !user) {
      if (isEmbedMode()) {
        // Embedded in the Tango portal: no interactive login exists here —
        // MSAL redirects are rejected inside an iframe (redirect_in_iframe).
        // The parent frame owns sign-in; all we can do is say so.
        setError('Not signed in. Please sign in to the host portal and reload.');
      } else {
        console.log('[UnauthorizedPage] Auto-triggering login...');
        handleLogin();
      }
    }
    // Only depend on loading to check when auth finishes loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // Only re-run when loading state changes, not on user changes

  // Signed-out standalone visitor being redirected to AAD: show a quiet
  // signing-in screen (or the error if the redirect failed), not Access
  // Denied — that copy is for signed-in users who lack permission.
  if (!user && !isEmbedMode()) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
        {error ? (
          <>
            <div
              style={{
                padding: '1rem',
                marginBottom: '1rem',
                backgroundColor: 'var(--color-danger-soft)',
                border: '1px solid var(--color-danger-soft)',
                borderRadius: '4px',
                color: 'var(--color-danger)',
              }}
            >
              {error}
            </div>
            <button
              onClick={handleLogin}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                backgroundColor: 'var(--blue-600)',
                color: 'var(--white)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              Sign In
            </button>
          </>
        ) : (
          <p style={{ color: 'var(--grey-500)', fontSize: '1.2rem' }}>Signing you in…</p>
        )}
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '4rem 2rem',
      textAlign: 'center',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫 Access Denied</h1>
      <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--grey-500)' }}>
        You do not have permission to access this resource.
      </p>
      <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: 'var(--grey-500)' }}>
        Please contact your system administrator to request access.
      </p>
      {user?.email && (
        <p style={{ marginBottom: '2rem', color: 'var(--grey-500)' }}>
          Signed in as {user.email}
        </p>
      )}
      {error && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: 'var(--color-danger-soft)',
          border: '1px solid var(--color-danger-soft)',
          borderRadius: '4px',
          color: 'var(--color-danger)'
        }}>
          {error}
        </div>
      )}
      {/* Sign In only helps a signed-OUT visitor (embed's no-user case);
          a signed-in-but-denied user re-running login just loops back here. */}
      {!user && !loading && !loginAttempted && (
        <button
          onClick={handleLogin}
          style={{
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            backgroundColor: 'var(--blue-600)',
            color: 'var(--white)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Sign In
        </button>
      )}
      {!user && (loading || loginAttempted) && (
        <p style={{ color: 'var(--grey-500)' }}>
          {loginAttempted ? 'Opening login window...' : 'Loading...'}
        </p>
      )}
    </div>
  );
};

export default UnauthorizedPage;

